import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Quality Module Service Stores
 * Services for quality control, testing, and compliance
 */
export const qualityStores: ServiceStoreConfig[] = [
  {
    name: "compression-tests",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.compressionTestsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalTests = entities.length;
          const passedTests = entities.filter(
            (test: Record<string, unknown>) =>
              test.result === "pass" || (test.strength as number) >= (test.requiredStrength as number || 3000)
          ).length;
          const failedTests = entities.filter(
            (test: Record<string, unknown>) =>
              test.result === "fail" || (test.strength as number) < (test.requiredStrength as number || 3000)
          ).length;
          const pendingTests = entities.filter(
            (test: Record<string, unknown>) =>
              test.status === "pending" || test.status === "scheduled"
          ).length;

          return [
            {
              title: "Total Tests",
              value: () => totalTests.toString(),
              valueColor: "primary.main",
              variant: "with-icon" as const,
            },
            {
              title: "Passed",
              value: () => passedTests.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failedTests.toString(),
              valueColor: "error.main",
            },
            {
              title: "Pending",
              value: () => pendingTests.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["testDate"],
          sortDirection: { testDate: "DESC" },
        },
      },
    },
  },
  {
    name: "material-tests",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialTestsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalTests = entities.length;
          const todayTests = entities.filter(
            (test: Record<string, unknown>) => {
              const testDate = new Date(test.createdAt as string);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return testDate >= today;
            }
          ).length;
          const passedTests = entities.filter(
            (test: Record<string, unknown>) =>
              test.result === "pass" || test.status === "approved"
          ).length;
          const failedTests = entities.filter(
            (test: Record<string, unknown>) =>
              test.result === "fail" || test.status === "rejected"
          ).length;

          return [
            {
              title: "Total Tests",
              value: () => totalTests.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todayTests.toString(),
              valueColor: "info.main",
            },
            {
              title: "Passed",
              value: () => passedTests.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failedTests.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["testDate"],
          sortDirection: { testDate: "DESC" },
        },
      },
    },
  },
  {
    name: "quality-control",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.qualityControlStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalChecks = entities.length;
          const approvedChecks = entities.filter(
            (check: Record<string, unknown>) =>
              check.status === "approved" || check.result === "pass"
          ).length;
          const rejectedChecks = entities.filter(
            (check: Record<string, unknown>) =>
              check.status === "rejected" || check.result === "fail"
          ).length;
          const pendingChecks = entities.filter(
            (check: Record<string, unknown>) =>
              check.status === "pending" || check.status === "in_review"
          ).length;

          return [
            {
              title: "Total Checks",
              value: () => totalChecks.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Approved",
              value: () => approvedChecks.toString(),
              valueColor: "success.main",
            },
            {
              title: "Rejected",
              value: () => rejectedChecks.toString(),
              valueColor: "error.main",
            },
            {
              title: "Pending",
              value: () => pendingChecks.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["createdAt"],
          sortDirection: { createdAt: "DESC" },
        },
      },
    },
  },
  {
    name: "slump-tests",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.slumpTestsStore as {
            entities?: Record<string, unknown>[];
          };
          const entities = store?.entities || [];
          const totalTests = entities.length;
          const withinSpecTests = entities.filter(
            (test) => {
              const slump = test.slumpValue as number;
              const min = test.minSlump as number || 3;
              const max = test.maxSlump as number || 5;
              return slump >= min && slump <= max;
            }
          ).length;
          const outOfSpecTests = entities.filter(
            (test) => {
              const slump = test.slumpValue as number;
              const min = test.minSlump as number || 3;
              const max = test.maxSlump as number || 5;
              return slump < min || slump > max;
            }
          ).length;
          const avgSlump = entities.reduce(
            (sum, test) => {
              return sum + ((test.slumpValue as number) || 0);
            },
            0
          ) / Math.max(entities.length, 1);

          return [
            {
              title: "Total Tests",
              value: () => totalTests.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Within Spec",
              value: () => withinSpecTests.toString(),
              valueColor: "success.main",
            },
            {
              title: "Out of Spec",
              value: () => outOfSpecTests.toString(),
              valueColor: "error.main",
            },
            {
              title: "Avg Slump (in)",
              value: () => avgSlump.toFixed(1),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["testDate"],
          sortDirection: { testDate: "DESC" },
        },
      },
    },
  },
];